import * as React from 'react';
import {
  ApprovalStatus,
  CampaignSendStatus,
  CampaignStatus,
  CheckStatus,
  ConsentStatus,
  CouponStatus,
  CustomerMergeStatus,
  CustomerOrderStatus,
  EmploymentStatus,
  EntitlementStatus,
  TimeEntryStatus,
  FeedbackStatus,
  KitchenTicketStatus,
  MenuVersionStatus,
  OrderItemStatus,
  OrderStatus,
  PaymentStatus,
  PurchaseOrderStatus,
  PurchaseReceiptStatus,
  RecipeVersionStatus,
  ReservationStatus,
  ResourceSessionStatus,
  ResourceStatus,
  RoyaltyStatementStatus,
  ServiceRequestStatus,
  SessionStatus,
  SubscriptionStatus,
  TableState,
  TakeawayStatus,
  WalletAccountStatus,
} from '@/lib/shared-types';
import { cn } from '@/lib/utils';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

/**
 * `success`, `warning`, `danger` and `neutral` are deliberately literal and
 * deliberately unchanged by the theme pass. They are what existing screens
 * *mean*, and re-pointing them at theme tokens is a product decision, not a
 * styling one. The consequence is visible and intended: on the dark theme these
 * four stay light chips. `brand` is chrome rather than state, so it follows the
 * accent.
 */
const TONES: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-700',
  info: 'bg-sky-100 text-sky-700',
  brand: 'bg-accent-soft text-accent-soft-fg',
};

export function Badge({
  tone = 'neutral',
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { tone?: Tone }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        TONES[tone],
        className,
      )}
      {...props}
    />
  );
}

/**
 * Every status-bearing enum in `@/lib/shared-types`, in one place.
 *
 * The list is what makes the table below checkable. It is not decoration: the
 * union derived from it is the `Record` key type, so a member that exists in the
 * package and not in the table is a **compile error here**, which is the whole
 * point. Enums that describe a *kind* rather than a *state* — `AreaType`,
 * `PaymentMethod`, `ChargeType` and the rest — are deliberately absent; they are
 * never rendered as a status chip and folding them in would make half these
 * names ambiguous.
 */
type StatusEnum =
  | typeof TableState
  | typeof ResourceStatus
  | typeof ResourceSessionStatus
  | typeof OrderStatus
  | typeof CampaignStatus
  | typeof CampaignSendStatus
  | typeof CustomerOrderStatus
  | typeof TakeawayStatus
  | typeof OrderItemStatus
  | typeof CheckStatus
  | typeof PaymentStatus
  | typeof ServiceRequestStatus
  | typeof SubscriptionStatus
  | typeof ReservationStatus
  | typeof PurchaseOrderStatus
  | typeof SessionStatus
  | typeof ApprovalStatus
  | typeof KitchenTicketStatus
  | typeof MenuVersionStatus
  | typeof EntitlementStatus
  | typeof RecipeVersionStatus
  | typeof RoyaltyStatementStatus
  | typeof PurchaseReceiptStatus
  | typeof FeedbackStatus
  | typeof CouponStatus
  | typeof WalletAccountStatus
  | typeof CustomerMergeStatus
  | typeof ConsentStatus
  | typeof TimeEntryStatus
  | typeof EmploymentStatus;

/** Distributive, so it is the union of every member of every enum above. */
type ValuesOf<T> = T extends unknown ? T[keyof T] : never;

/** Every status any screen can be handed. */
export type DomainStatus = ValuesOf<StatusEnum>;

/**
 * Status → tone, and the reason it is a table rather than a `switch`.
 *
 * The `switch` this replaces ended in `default: return 'neutral'`, so a member
 * nobody had thought about rendered as a grey chip — visually identical to a
 * status the app does not recognise, and identical to the states that are
 * *meant* to be grey. `PICKED_UP` was the one a user actually reported: a line
 * carried away from the pass looked exactly like an unknown string. Sixty-odd
 * members across twenty-two enums were in the same position, `AWAITING_PICKUP`,
 * `QUEUED`, `HANDED_OVER`, `NEW` and `RECALLED` among them.
 *
 * `satisfies Record<DomainStatus, Tone> & Record<string, Tone>` is the pair of
 * guarantees that keeps it that way:
 *  - the first half makes **every** member of every enum above a required key,
 *    so adding one to shared-types breaks this build instead of adding a silent
 *    grey chip;
 *  - the second half permits keys that are not in the union *yet*, which is what
 *    lets `COLLECTED` be answered below before the package that declares it has
 *    shipped. Without it, either this file or the package would have to land
 *    first, and only one of them is editable from here.
 *
 * Neutral is now a decision. `DRAFT`, `TEMPLATE`, `ARCHIVED`, `CLOSED`,
 * `CONSUMED`, `REDEEMED`, `DISMISSED`, `DISABLED` and `ENDED` are inert — over,
 * or not yet begun, and nothing is owed. That is a different statement from
 * "this string means nothing to me", which is what `unknownTone` says.
 */
const STATUS_TONES = {
  // ---- live, in progress, someone is on it
  OPEN: 'info',
  OCCUPIED: 'info',
  IN_USE: 'info',
  SENT_TO_KITCHEN: 'info',
  RELEASED: 'info',
  RESERVED: 'info',
  ORDER_RECEIVED: 'info',
  CREATED: 'info',
  ACCEPTED: 'info',
  SUBMITTED: 'info',
  ISSUED: 'info',
  TRIAL: 'info',
  ACKNOWLEDGED: 'info',
  /** A ticket nobody has started. It is work, and it is waiting. */
  NEW: 'info',
  /** Accepted by the station and waiting its turn — in the system, not started. */
  QUEUED: 'info',
  /** ADR-0026 — a campaign the sweep is actively walking its segment for. */
  SENDING: 'info',
  /**
   * §15.2 — off the pass, not yet on the table.
   *
   * The one the user reported. It is forward motion and it is not the end
   * (`SERVED` is), so it must read as neither "done" nor "unrecognised".
   */
  PICKED_UP: 'info',

  // ---- someone has to do something
  PREPARING: 'warning',
  PENDING: 'warning',
  /** ADR-0008 — a closed timesheet nobody has signed yet. Every closed entry passes through here. */
  PENDING_APPROVAL: 'warning',
  /** §20 — waiting on an approver who is, by rule, never its own author. */
  IN_REVIEW: 'warning',
  PAYMENT_PENDING: 'warning',
  PARTIALLY_PAID: 'warning',
  PARTIALLY_READY: 'warning',
  PARTIALLY_RECEIVED: 'warning',
  PARTIALLY_RETURNED: 'warning',
  PARTIALLY_CONSUMED: 'warning',
  PARTIALLY_REFUNDED: 'warning',
  PAUSED: 'warning',
  SCHEDULED: 'warning',
  IN_RECOVERY: 'warning',
  /** Blocks the table until somebody clears it. */
  CLEANING: 'warning',
  /** Money held, not captured — the gap a shift report has to close. */
  AUTHORIZED: 'warning',
  /** ADR-0012 — frozen pending a fraud review. Reads still work; only the money stops moving. */
  SUSPENDED: 'warning',
  /**
   * Cooked, plated, and sitting on the pass with nobody carrying it. This is the
   * state the pass exists to make loud.
   */
  AWAITING_PICKUP: 'warning',
  /** ADR-0026 — the customer had no email/phone on file; a data gap, not a system failure. */
  SKIPPED_INVALID_RECIPIENT: 'warning',

  // ---- done, and done well
  AVAILABLE: 'success',
  ACTIVE: 'success',
  PAID: 'success',
  COMPLETED: 'success',
  READY: 'success',
  SERVED: 'success',
  CONFIRMED: 'success',
  RECEIVED: 'success',
  RESOLVED: 'success',
  APPROVED: 'success',
  DONE: 'success',
  SEATED: 'success',
  /** The bag reached the customer — the takeaway queue's terminal good state. */
  HANDED_OVER: 'success',
  /** A ticket every line of which has been carried away. */
  COLLECTED: 'success',
  /** §20 — a menu version live to price resolution right now. */
  PUBLISHED: 'success',
  /** ADR-0023 §7 — a royalty statement no route moves to yet; reachable once one does. */
  FINAL: 'success',
  /** ADR-0010 — a guest explicitly said yes on this channel. */
  GRANTED: 'success',
  /** ADR-0026 — one customer's outcome: the message actually went out. */
  SENT: 'success',

  // ---- wrong, refused, or lost
  CANCELLED: 'danger',
  VOIDED: 'danger',
  REFUNDED: 'danger',
  FAILED: 'danger',
  NO_SHOW: 'danger',
  ESCALATED: 'danger',
  OUT_OF_SERVICE: 'danger',
  REJECTED: 'danger',
  EXPIRED: 'danger',
  PAST_DUE: 'danger',
  RETURNED: 'danger',
  BLOCKED: 'danger',
  MAINTENANCE: 'danger',
  /**
   * §16 — food came back to be remade. A correction, and the loudest thing on
   * the board: it is the only status here that means work already called done
   * has to be done again.
   */
  RECALLED: 'danger',
  /**
   * §20 — a menu version corrected by a rollback. Stays LIVE to resolution
   * (ADR-0011 §5.1) so the checks written under it still reconcile, but this
   * colour says the same thing RECALLED does: work already counted done had to
   * be undone.
   */
  ROLLED_BACK: 'danger',
  /** ADR-0010 — a refusal always wins the merge reconciliation; treated like DENIED. */
  WITHDRAWN: 'danger',
  DENIED: 'danger',

  // ---- inert: over, or not yet begun, and nothing is owed
  DRAFT: 'neutral',
  CLOSED: 'neutral',
  /** ADR-0012 — a printed gift card nobody has bought yet. Worth nothing until activated. */
  INACTIVE: 'neutral',
  ENDED: 'neutral',
  /** ADR-0008 — employment over. The record and its timesheets are kept; nothing is owed by this badge. */
  TERMINATED: 'neutral',
  TEMPLATE: 'neutral',
  CONSUMED: 'neutral',
  ARCHIVED: 'neutral',
  DISMISSED: 'neutral',
  REDEEMED: 'neutral',
  DISABLED: 'neutral',
  /** ADR-0010 — undone, not wrong; the merge it undoes stays `COMPLETED` on its own row. */
  REVERSED: 'neutral',
  /**
   * ADR-0026 §4 — the gate working as designed: no GRANTED consent on file at
   * send time. Not `danger` — a campaign respecting a refusal is the system
   * doing its job, not a failure.
   */
  SKIPPED_NO_CONSENT: 'neutral',
  /** ADR-0026 §6 — the weekly fatigue cap protecting this customer from over-messaging. Also not a failure. */
  SKIPPED_FATIGUE_CAP: 'neutral',
} satisfies Record<DomainStatus, Tone> & Record<string, Tone>;

/**
 * What a string this table has never heard of gets.
 *
 * Deliberately the same grey as the inert states, because inventing a sixth
 * colour for "unknown" would put a chip on screen that means nothing to anyone —
 * but it is reached only by a value that came from outside the enums above, and
 * in dev it says so rather than blending in.
 */
const unknownTone: Tone = 'neutral';

/**
 * Map any domain status onto a tone, so state reads the same on every screen.
 *
 * The signature stays loose — callers hand this `order.status` off an API
 * response, which is `string` — while the table it reads is exhaustively typed.
 * That is the split that matters: the runtime tolerates anything, the mapping
 * tolerates nothing.
 */
export function toneForStatus(status?: string | null): Tone {
  if (!status) return unknownTone;
  const tone = (STATUS_TONES as Record<string, Tone>)[status];
  if (tone) return tone;
  if (import.meta.env.DEV) {
    console.warn(
      `[badge] no tone for status "${status}" — it renders as a grey chip, ` +
        'indistinguishable from a state that is meant to be grey. Add it to STATUS_TONES.',
    );
  }
  return unknownTone;
}
